import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { showToast } from '@/lib/custom-toast';
import { formatCurrencyInput, parseCurrency } from '@/utils/currencyFormat';
import MobileBottomSheet from '@/components/MobileBottomSheet';
import {
  Bell,
  Plus,
  Edit2,
  Trash2,
  Calendar,
  DollarSign,
  CreditCard,
  Zap,
  Phone,
  Home,
  Package,
  MoreHorizontal,
  MoreVertical,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  Link2,
  ArrowRight,
  Film,
  GraduationCap,
  Shield,
  Landmark,
  Building2,
  X
} from 'lucide-react';

const CATEGORIES = [
  { value: 'utilities', label: 'Servicios (Luz, Agua, Gas)', icon: Home },
  { value: 'credit_card', label: 'Tarjeta de Crédito', icon: CreditCard },
  { value: 'subscription', label: 'Suscripciones (Netflix, Spotify)', icon: Film },
  { value: 'education', label: 'Educación (Colegiaturas)', icon: GraduationCap },
  { value: 'insurance', label: 'Seguros', icon: Shield },
  { value: 'loan', label: 'Préstamos', icon: Landmark },
  { value: 'rent', label: 'Renta', icon: Building2 },
  { value: 'other', label: 'Otros', icon: DollarSign }
];

export const isLinkedAccountReminder = (reminder) => {
  if (!reminder) return false;
  return (
    reminder.source === 'account' ||
    Boolean(reminder.linked_account_id) ||
    (typeof reminder.description === 'string' && reminder.description.includes('creado desde Cuentas'))
  );
};

export const isLinkedDebtReminder = (reminder) => {
  if (!reminder) return false;
  return (
    reminder.source === 'debt' ||
    Boolean(reminder.debt_id)
  );
};

export const isNonAdminReminder = (reminder, userId) => {
  if (!reminder) return false;
  return Boolean(reminder.admin_id && userId && reminder.admin_id !== userId);
};

export const isReadOnlyReminder = (reminder, userId) => {
  if (!reminder) return false;
  if (isLinkedAccountReminder(reminder)) return true;
  if (isNonAdminReminder(reminder, userId)) return true;
  return false;
};

const PaymentReminders = () => {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [reminders, setReminders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState(null);
  const [selectedReminderForActions, setSelectedReminderForActions] = useState(null);

  // Drag down to close reminder modal on mobile
  const [reminderDragStartY, setReminderDragStartY] = useState(0);
  const [reminderDragCurrentY, setReminderDragCurrentY] = useState(0);
  const [isReminderDragging, setIsReminderDragging] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    category: 'other',
    frequency: 'monthly',
    due_date: 1,
    start_month: new Date().getMonth() + 1,
    start_year: new Date().getFullYear(),
    notification_days: 3,
    notification_time: '09:00',
    all_day: false
  });

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await fetch(`${API}/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching accounts for reminders:', error);
    }
  }, [token]);

  const fetchReminders = useCallback(async () => {
    try {
      const response = await fetch(`${API}/payment-reminders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar recordatorios');
      }

      const data = await response.json();
      setReminders(data);
    } catch (error) {
      console.error('Error fetching reminders:', error);
      showToast.error('Error al cargar recordatorios');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchReminders();
      fetchAccounts();
    }
  }, [token, fetchReminders, fetchAccounts]);

  // Lock body scroll when reminder modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const getLinkedAccountId = useCallback((reminder) => {
    if (!reminder) return null;
    if (reminder.linked_account_id) return reminder.linked_account_id;
    // Fallback: coincidencia por nombre de cuenta si el recordatorio fue creado previamente sin linked_account_id
    if (accounts.length > 0) {
      const titleLower = (reminder.title || '').toLowerCase().trim();
      const found = accounts.find(acc => {
        const accName = (acc.name || '').toLowerCase().trim();
        return titleLower === `pago ${accName}` || titleLower.includes(accName);
      });
      if (found) return found.id || found._id;
    }
    return null;
  }, [accounts]);

  const handleGoToAccount = (reminder) => {
    const accId = getLinkedAccountId(reminder);
    if (accId) {
      navigate(`/accounts/${accId}`);
    } else {
      navigate('/accounts');
    }
  };

  const handleEditInAccount = (reminder) => {
    const accId = getLinkedAccountId(reminder);
    if (accId) {
      navigate(`/accounts?editAccountId=${accId}&highlight=reminder`);
    } else {
      navigate('/accounts');
    }
  };

  const handleOpenModal = (reminder = null) => {
    if (reminder) {
      setEditingReminder(reminder);
      setFormData({
        title: reminder.title,
        description: reminder.description || '',
        amount: reminder.amount || '',
        category: reminder.category,
        frequency: reminder.frequency || 'monthly',
        due_date: reminder.due_date,
        start_month: reminder.start_month || new Date().getMonth() + 1,
        start_year: reminder.start_year || new Date().getFullYear(),
        notification_days: reminder.notification_days,
        notification_time: reminder.notification_time || '09:00',
        all_day: reminder.all_day || false
      });
    } else {
      setEditingReminder(null);
      setFormData({
        title: '',
        description: '',
        amount: '',
        category: 'other',
        frequency: 'monthly',
        due_date: 1,
        start_month: new Date().getMonth() + 1,
        start_year: new Date().getFullYear(),
        notification_days: 3,
        notification_time: '09:00',
        all_day: false
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingReminder(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let payload;
      if (editingReminder && isNonAdminReminder(editingReminder, user?.id)) {
        payload = {
          all_day: formData.all_day,
          notification_time: formData.all_day ? "09:00" : formData.notification_time
        };
      } else {
        payload = {
          title: formData.title,
          description: formData.description || null,
          amount: formData.amount ? parseFloat(formData.amount) : null,
          category: formData.category,
          frequency: formData.frequency,
          due_date: parseInt(formData.due_date),
          start_month: formData.frequency !== 'monthly' ? parseInt(formData.start_month) : null,
          start_year: formData.frequency !== 'monthly' ? parseInt(formData.start_year) : null,
          notification_days: parseInt(formData.notification_days),
          notification_time: formData.all_day ? "09:00" : formData.notification_time,
          all_day: formData.all_day
        };
      }

      const url = editingReminder
        ? `${API}/payment-reminders/${editingReminder.id}`
        : `${API}/payment-reminders`;

      const method = editingReminder ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar recordatorio');
      }

      showToast.success(editingReminder ? 'Recordatorio actualizado' : 'Recordatorio creado');
      handleCloseModal();
      fetchReminders();
    } catch (error) {
      console.error('Error saving reminder:', error);
      showToast.error(error.message);
    }
  };

  const handleToggle = async (reminder) => {
    try {
      const response = await fetch(`${API}/payment-reminders/${reminder.id}/toggle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cambiar estado');
      }

      showToast.success(reminder.is_active ? 'Recordatorio desactivado' : 'Recordatorio activado');
      fetchReminders();
    } catch (error) {
      console.error('Error toggling reminder:', error);
      showToast.error(error.message);
    }
  };

  const handleDelete = async () => {
    if (!reminderToDelete) return;

    try {
      const response = await fetch(`${API}/payment-reminders/${reminderToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al eliminar recordatorio');
      }

      showToast.success('Recordatorio eliminado');
      setShowDeleteConfirm(false);
      setReminderToDelete(null);
      fetchReminders();
    } catch (error) {
      console.error('Error deleting reminder:', error);
      showToast.error(error.message);
    }
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : MoreHorizontal;
  };

  const getCategoryLabel = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.label : 'Otro';
  };

  const getFrequencyLabel = (frequency) => {
    const frequencies = {
      'monthly': 'Mensual',
      'bimonthly': 'Bimestral',
      'quarterly': 'Trimestral',
      'semiannual': 'Semestral',
      'annual': 'Anual'
    };
    return frequencies[frequency] || 'Mensual';
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const currentDay = today.getDate();
    let daysUntil = dueDate - currentDay;

    if (daysUntil < 0) {
      // Calculate days until next month
      const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      daysUntil = (daysInMonth - currentDay) + dueDate;
    }

    return daysUntil;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-2">
                Recordatorios de Pago
              </h1>
              <p className="text-sm md:text-base text-slate-600 dark:text-slate-400">
                Nunca olvides un pago importante
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleOpenModal()}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-bold text-sm shadow-md shadow-blue-500/20 active:scale-95 transition-all cursor-pointer"
            >
              <div className="w-6 h-6 rounded-xl bg-white/20 flex items-center justify-center">
                <Plus className="w-4 h-4" />
              </div>
              <span>Nuevo Recordatorio</span>
            </button>
          </div>
        </div>        {/* Info Banner Modernizado */}
        <div className="mb-6 p-3.5 sm:p-4 bg-gradient-to-r from-blue-50/90 to-indigo-50/90 dark:from-blue-950/40 dark:to-indigo-950/40 border border-blue-200/70 dark:border-blue-800/60 rounded-2xl flex items-center justify-between gap-3 shadow-xs">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-xs">
              <Bell className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white">
                Notificaciones automáticas activas
              </p>
              <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 truncate">
                Recibirás recordatorio push y por email antes de cada vencimiento
              </p>
            </div>
          </div>
        </div>

        {/* Reminders List */}
        {reminders.length === 0 ? (
          <Card className="p-12 text-center rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-xs">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <Calendar className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
              No tienes recordatorios
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">
              Crea tu primer recordatorio para tener el control de tus fechas de corte y pagos importantes.
            </p>
            <button
              type="button"
              onClick={() => handleOpenModal()}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-bold text-sm shadow-md shadow-blue-500/20 active:scale-95 transition-all cursor-pointer"
            >
              <Plus className="w-5 h-5" />
              <span>Crear Recordatorio</span>
            </button>
          </Card>
        ) : (
          <div className="grid gap-2.5 sm:gap-3">
            {reminders.map((reminder) => {
              const IconComponent = getCategoryIcon(reminder.category);
              const daysUntil = getDaysUntilDue(reminder.due_date);
              const isUrgent = daysUntil <= reminder.notification_days && daysUntil >= 0;
              const isLinkedAcc = isLinkedAccountReminder(reminder);
              const isLinkedDebt = isLinkedDebtReminder(reminder);

              return (
                <Card
                  key={reminder.id}
                  className={`p-3 sm:p-4 rounded-2xl border transition-all duration-200 bg-white dark:bg-slate-800/95 shadow-xs hover:shadow-md relative overflow-hidden ${
                    !reminder.is_active ? 'opacity-60 bg-slate-50/50 dark:bg-slate-900/40' : ''
                  } ${
                    isUrgent && reminder.is_active
                      ? 'border-l-4 border-l-amber-500 border-amber-200/80 dark:border-amber-900/60'
                      : 'border-slate-200/80 dark:border-slate-700/60'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    {/* Icono de categoría */}
                    <div className="w-11 h-11 sm:w-12 sm:h-12 min-w-[44px] sm:min-w-[48px] rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/60 dark:to-indigo-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 shadow-xs border border-blue-100/60 dark:border-blue-900/50">
                      <IconComponent className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>

                    {/* Información Principal */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white truncate">
                          {reminder.title}
                        </h3>
                        {isUrgent && reminder.is_active && (
                          <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-200 text-[10px] font-extrabold rounded-full shrink-0 animate-pulse">
                            Vence en {daysUntil === 0 ? 'hoy' : `${daysUntil}d`}
                          </span>
                        )}
                      </div>

                      {/* Sub-fila de detalles limpios */}
                      <div className="flex items-center gap-2 text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex-wrap">
                        <span className="font-semibold text-slate-700 dark:text-slate-300">
                          Día {reminder.due_date} ({getFrequencyLabel(reminder.frequency || 'monthly')})
                        </span>
                        <span>·</span>
                        <span>
                          🔔 {reminder.notification_days}d antes ({reminder.all_day ? 'Todo el día' : (reminder.notification_time || '09:00')})
                        </span>
                      </div>

                      {/* Botones de acceso directo a la cuenta vinculada: Ir a cuenta + Editar en cuenta */}
                      {isLinkedAcc && (
                        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGoToAccount(reminder);
                            }}
                            className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:hover:bg-rose-900/50 dark:text-rose-300 border border-rose-200/80 dark:border-rose-800 active:scale-95 transition-all shadow-2xs"
                            title="Ir al detalle de la cuenta vinculada"
                          >
                            <Link2 className="w-3 h-3 text-rose-500" />
                            <span>Ir a la cuenta</span>
                            <ArrowRight className="w-3 h-3 text-rose-500 ml-0.5" />
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditInAccount(reminder);
                            }}
                            className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 active:scale-95 transition-all shadow-2xs"
                            title="Editar recordatorio directamente en la cuenta vinculada"
                          >
                            <Edit2 className="w-3 h-3 text-slate-500 dark:text-slate-400" />
                            <span>Editar recordatorio en cuenta</span>
                          </button>
                        </div>
                      )}

                      {isLinkedDebt && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/debts');
                            }}
                            className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:hover:bg-indigo-900/50 dark:text-indigo-300 border border-indigo-200/80 dark:border-indigo-800 active:scale-95 transition-all shadow-2xs"
                            title="Ver deuda asociada"
                          >
                            <span>Ver en Deudas</span>
                            <ArrowRight className="w-3 h-3 text-indigo-500 ml-0.5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Monto y Botonera rápida a la derecha */}
                    <div className="flex items-center gap-2 shrink-0">
                      {reminder.amount && (
                        <div className="text-right hidden sm:block">
                          <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Monto</p>
                          <p className="text-sm sm:text-base font-black text-slate-900 dark:text-white">
                            ${reminder.amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      )}

                      {/* Botón Toggle de Estado */}
                      <button
                        type="button"
                        onClick={() => handleToggle(reminder)}
                        className={`p-1.5 rounded-xl transition-all active:scale-90 ${
                          reminder.is_active
                            ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40'
                            : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                        title={reminder.is_active ? 'Recordatorio activo (clic para desactivar)' : 'Recordatorio inactivo (clic para activar)'}
                        aria-label="Cambiar estado del recordatorio"
                      >
                        {reminder.is_active ? (
                          <ToggleRight className="w-6 h-6" />
                        ) : (
                          <ToggleLeft className="w-6 h-6" />
                        )}
                      </button>

                      {/* Botón Opciones (Abre ActionSheet) */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.currentTarget?.blur();
                          setSelectedReminderForActions(reminder);
                        }}
                        className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors active:scale-95"
                        title="Más opciones"
                        aria-label="Más opciones"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        {isModalOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}
          >
            {/* Bottom sheet en móvil, modal centrado en desktop */}
            <div
              style={{
                transform: isReminderDragging && reminderDragCurrentY > 0 ? `translateY(${reminderDragCurrentY}px)` : undefined,
                transition: isReminderDragging ? 'none' : 'transform 0.2s ease-out'
              }}
              className="bg-white dark:bg-slate-800 w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl max-h-[93vh] sm:max-h-[90vh] overflow-y-auto"
            >
              {/* Handle bar para arrastrar hacia abajo visible en móvil - sticky para no perderse al hacer scroll */}
              <div
                onTouchStart={(e) => {
                  setReminderDragStartY(e.touches[0].clientY);
                  setIsReminderDragging(true);
                }}
                onTouchMove={(e) => {
                  if (!isReminderDragging) return;
                  const diff = e.touches[0].clientY - reminderDragStartY;
                  if (diff > 0) setReminderDragCurrentY(diff);
                }}
                onTouchEnd={() => {
                  setIsReminderDragging(false);
                  if (reminderDragCurrentY > 75) {
                    handleCloseModal();
                  }
                  setReminderDragCurrentY(0);
                }}
                className="sticky top-0 z-20 flex justify-center pt-3 pb-2 sm:hidden cursor-grab active:cursor-grabbing select-none bg-white dark:bg-slate-800 rounded-t-3xl"
              >
                <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full" />
              </div>

              <div className="p-6 pt-2 sm:pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                    {editingReminder ? 'Editar Recordatorio' : 'Nuevo Recordatorio'}
                  </h2>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Banner si el recordatorio está vinculado a cuenta */}
                {isLinkedAccountReminder(editingReminder) && (
                  <div className="mb-4 p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl">
                    <div className="flex items-start gap-2">
                      <Link2 className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-rose-800 dark:text-rose-200">
                        <p className="font-bold mb-0.5">Recordatorio vinculado a cuenta</p>
                        <p>Los datos de título, categoría, monto y fecha de corte están protegidos. Para editarlos, ve a Cuentas.</p>
                        <button
                          type="button"
                          onClick={() => {
                            handleCloseModal();
                            handleGoToAccount(editingReminder);
                          }}
                          className="mt-1.5 text-rose-600 dark:text-rose-400 font-semibold flex items-center gap-1 hover:underline"
                        >
                          <ArrowRight className="w-3 h-3" />
                          Ir a la cuenta vinculada para editar datos
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {isNonAdminReminder(editingReminder, user?.id) && (
                  <div className="mb-4 p-3.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2.5">
                    <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <strong className="block">Recordatorio vinculado a deuda:</strong>
                      <span>Los montos, fechas y conceptos están gestionados por el creador original. Puedes personalizar la <strong>hora de notificación</strong> de tu cuenta.</span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Título *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Ej: Tarjeta BBVA, Luz CFE"
                      required
                      readOnly={isReadOnlyReminder(editingReminder, user?.id)}
                      className={isReadOnlyReminder(editingReminder, user?.id) ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 cursor-not-allowed' : ''}
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Descripción</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Detalles adicionales (opcional)"
                      readOnly={isReadOnlyReminder(editingReminder, user?.id)}
                      className={isReadOnlyReminder(editingReminder, user?.id) ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 cursor-not-allowed' : ''}
                    />
                  </div>

                  <div>
                    <Label htmlFor="category">Categoría</Label>
                    <Select
                      value={formData.category}
                      disabled={isReadOnlyReminder(editingReminder, user?.id)}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger className={isReadOnlyReminder(editingReminder, user?.id) ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 cursor-not-allowed' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="amount">Monto (opcional)</Label>
                    <Input
                      id="amount"
                      type="text"
                      disabled={isReadOnlyReminder(editingReminder, user?.id)}
                      value={formData.amount ? formatCurrencyInput(formData.amount) : ''}
                      onChange={(e) => {
                        const rawValue = parseCurrency(e.target.value);
                        setFormData({ ...formData, amount: rawValue });
                      }}
                      placeholder="$0.00"
                      className={isReadOnlyReminder(editingReminder, user?.id) ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 cursor-not-allowed' : ''}
                    />
                  </div>

                  <div>
                    <Label htmlFor="frequency">Frecuencia de Pago *</Label>
                    <select
                      id="frequency"
                      value={formData.frequency}
                      disabled={isReadOnlyReminder(editingReminder, user?.id)}
                      onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                      className={`w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isReadOnlyReminder(editingReminder, user?.id) ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 cursor-not-allowed' : ''
                      }`}
                      required
                    >
                      <option value="monthly">Mensual</option>
                      <option value="bimonthly">Bimestral</option>
                      <option value="quarterly">Trimestral</option>
                      <option value="semiannual">Semestral</option>
                      <option value="annual">Anual</option>
                    </select>
                  </div>

                  {/* Fecha de inicio - Solo para frecuencias NO mensuales */}
                  {formData.frequency !== 'monthly' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="start_month">Mes de Inicio *</Label>
                        <select
                          id="start_month"
                          value={formData.start_month}
                          onChange={(e) => setFormData({ ...formData, start_month: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        >
                          <option value="1">Enero</option>
                          <option value="2">Febrero</option>
                          <option value="3">Marzo</option>
                          <option value="4">Abril</option>
                          <option value="5">Mayo</option>
                          <option value="6">Junio</option>
                          <option value="7">Julio</option>
                          <option value="8">Agosto</option>
                          <option value="9">Septiembre</option>
                          <option value="10">Octubre</option>
                          <option value="11">Noviembre</option>
                          <option value="12">Diciembre</option>
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="start_year">Año *</Label>
                        <Input
                          id="start_year"
                          type="number"
                          min={new Date().getFullYear()}
                          max={new Date().getFullYear() + 10}
                          value={formData.start_year}
                          onChange={(e) => setFormData({ ...formData, start_year: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="due_date">{formData.frequency === 'monthly' ? 'Día de Vencimiento (1-31) *' : 'Día del Mes *'}</Label>
                    <Input
                      id="due_date"
                      type="number"
                      min="1"
                      max="31"
                      disabled={isReadOnlyReminder(editingReminder, user?.id)}
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      required
                      className={isReadOnlyReminder(editingReminder, user?.id) ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 cursor-not-allowed' : ''}
                    />
                  </div>

                  <div>
                    <Label htmlFor="notification_days">Notificar con cuántos días de anticipación *</Label>
                    <Input
                      id="notification_days"
                      type="number"
                      min="0"
                      max="15"
                      disabled={isReadOnlyReminder(editingReminder, user?.id)}
                      value={formData.notification_days}
                      onChange={(e) => setFormData({ ...formData, notification_days: e.target.value })}
                      required
                      className={isReadOnlyReminder(editingReminder, user?.id) ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 cursor-not-allowed' : ''}
                    />
                  </div>

                  {/* Toggle Todo el Día */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div className="flex-1">
                      <Label className="text-sm font-medium">Todo el día</Label>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {formData.all_day ? 'Notificará a las 9:00 AM' : 'Elige hora específica'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, all_day: !formData.all_day })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.all_day ? 'bg-blue-600' : 'bg-slate-300'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.all_day ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>

                  {/* Hora de Notificación - Solo si NO es todo el día */}
                  {!formData.all_day && (
                    <div>
                      <Label htmlFor="notification_time">Hora de notificación *</Label>
                      <Input
                        id="notification_time"
                        type="time"
                        value={formData.notification_time}
                        onChange={(e) => setFormData({ ...formData, notification_time: e.target.value })}
                        required={!formData.all_day}
                        className="text-base"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Recibirás el recordatorio a esta hora (formato 24h)
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="flex-1 py-3 px-4 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 font-bold text-sm transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-sm shadow-md shadow-blue-500/20 active:scale-95 transition-all cursor-pointer"
                    >
                      {editingReminder ? 'Actualizar' : 'Crear'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && reminderToDelete && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                  ¿Eliminar recordatorio?
                </h3>
              </div>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                ¿Estás seguro de eliminar el recordatorio "{reminderToDelete.title}"? Esta acción no se puede deshacer.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setReminderToDelete(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Eliminar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ─── MODAL BOTTOM-SHEET: ACCIONES DE RECORDATORIO ─── */}
        <MobileBottomSheet
          isOpen={!!selectedReminderForActions}
          onClose={() => setSelectedReminderForActions(null)}
        >
          {({ close }) => {
            if (!selectedReminderForActions) return null;
            const r = selectedReminderForActions;
            const Icon = getCategoryIcon(r.category);
            const isLinkedAcc = isLinkedAccountReminder(r);
            const isLinkedDebt = isLinkedDebtReminder(r);

            return (
              <div className="space-y-3">
                {/* Cabecera del recordatorio */}
                <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-2xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 shadow-xs border border-blue-100/60 dark:border-blue-900/40">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-base text-slate-900 dark:text-white truncate">
                        {r.title}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {getCategoryLabel(r.category)} · Día {r.due_date} ({getFrequencyLabel(r.frequency || 'monthly')})
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={close}
                    className="p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Resumen del recordatorio */}
                <div className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-2xl flex items-center justify-between text-xs">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Notificación</span>
                    <span className="font-semibold text-slate-700 dark:text-slate-300">
                      {r.notification_days} días antes {r.all_day ? '(Todo el día)' : `a las ${r.notification_time || '09:00'}`}
                    </span>
                  </div>
                  {r.amount && (
                    <div className="text-right">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Monto</span>
                      <span className="font-black text-sm text-slate-900 dark:text-white">
                        ${r.amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Acciones */}
                <div className="space-y-1.5 pt-1">
                  {/* Ir a la cuenta vinculada */}
                  {isLinkedAcc && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.currentTarget?.blur();
                          close();
                          setTimeout(() => handleGoToAccount(r), 240);
                        }}
                        className="w-full p-3 rounded-2xl bg-rose-50/80 dark:bg-rose-950/40 hover:bg-rose-100 text-rose-700 dark:text-rose-300 border border-rose-200/80 dark:border-rose-800 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                      >
                        <div className="w-9 h-9 rounded-xl bg-rose-100 dark:bg-rose-900/60 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                          <ArrowRight className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold">Ir a la Cuenta Vinculada</p>
                          <p className="text-xs opacity-80 truncate">Ver detalles, saldo y movimientos de esta cuenta</p>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.currentTarget?.blur();
                          close();
                          setTimeout(() => handleEditInAccount(r), 240);
                        }}
                        className="w-full p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200/80 dark:border-slate-700 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                      >
                        <div className="w-9 h-9 rounded-xl bg-rose-100/70 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                          <Edit2 className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold">Editar Recordatorio en Cuenta</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Abre la edición de la cuenta resaltando el recordatorio</p>
                        </div>
                      </button>
                    </>
                  )}

                  {/* Ir a Deudas */}
                  {isLinkedDebt && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.currentTarget?.blur();
                        close();
                        setTimeout(() => navigate('/debts'), 240);
                      }}
                      className="w-full p-3 rounded-2xl bg-indigo-50/80 dark:bg-indigo-950/40 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 border border-indigo-200/80 dark:border-indigo-800 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                    >
                      <div className="w-9 h-9 rounded-xl bg-indigo-100 dark:bg-indigo-900/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                        <ArrowRight className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold">Ver en Deudas</p>
                        <p className="text-xs opacity-80 truncate">Consultar participantes, abonos y estado de la deuda</p>
                      </div>
                    </button>
                  )}

                  {/* Activar / Desactivar */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.currentTarget?.blur();
                      close();
                      handleToggle(r);
                    }}
                    className="w-full p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                      r.is_active ? 'bg-amber-50 text-amber-600 dark:bg-amber-950/50' : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50'
                    }`}>
                      {r.is_active ? <ToggleLeft className="w-4 h-4" /> : <ToggleRight className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">
                        {r.is_active ? 'Pausar Recordatorio' : 'Activar Recordatorio'}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {r.is_active ? 'No se enviarán notificaciones' : 'Recibirás avisos antes del vencimiento'}
                      </p>
                    </div>
                  </button>

                  {/* Editar */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.currentTarget?.blur();
                      close();
                      if (isLinkedAcc) {
                        showToast.info('Este recordatorio se administra directamente desde tu cuenta.');
                      }
                      setTimeout(() => handleOpenModal(r), 240);
                    }}
                    className="w-full p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                      <Edit2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">
                        {isNonAdminReminder(r, user?.id) ? 'Personalizar Hora' : 'Editar Recordatorio'}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {isLinkedAcc ? 'Ver configuración o ajustar hora de alerta' : 'Nombre, montos, fecha y frecuencia'}
                      </p>
                    </div>
                  </button>

                  {/* Eliminar */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.currentTarget?.blur();
                      close();
                      setTimeout(() => {
                        setReminderToDelete(r);
                        setShowDeleteConfirm(true);
                      }, 240);
                    }}
                    className="w-full p-3 rounded-2xl hover:bg-red-50/60 dark:hover:bg-red-950/30 border border-red-200/60 dark:border-red-900/40 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-red-50 dark:bg-red-950/50 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-red-600 dark:text-red-400">Eliminar Recordatorio</p>
                      <p className="text-xs text-red-500/80 truncate">Remover permanentemente esta alerta</p>
                    </div>
                  </button>
                </div>
              </div>
            );
          }}
        </MobileBottomSheet>
      </div>
    </Layout>
  );
};

export default PaymentReminders;